import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  Tooltip,
  createStyles,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { FileCopy as CopyIcon } from "@material-ui/icons";
import { QRCodeCanvas } from "qrcode.react";
import { useFlags } from "flagsmith/react";
import { PixPayload } from "../models/IPixPayment";
import { CircularProgress } from "@mui/material";

export type PixPaymentPanelProps = {
  razaoSocial: string;
  produto: string;
  valorTotal?: number;
  payCond?: number;
  payloads: PixPayload[];
  loading: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      border: "1px solid #eee",
      borderRadius: 12,
      padding: 16,
      background: "#fff",
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
      gap: 12,
      flexWrap: "wrap",
    },
    subHeader: { color: "#666" },
    card: {
      border: "1px solid #eee",
      borderRadius: 10,
      padding: 14,
      height: "100%",
    },
    codeBox: {
      background: "#f7f7f8",
      borderRadius: 8,
      padding: 10,
      wordBreak: "break-all",
      fontFamily: "monospace",
      fontSize: 13,
      border: "1px dashed #ddd",
    },
    statusPaid: {
      background: "#E8F5E9",
      color: "#2E7D32",
      fontWeight: 600,
    },
    statusPending: {
      background: "#FFF8E1",
      color: "#8D6E63",
      fontWeight: 600,
    },
    actions: {
      display: "flex",
      gap: 8,
      marginTop: 10,
    },
    noCodesMessage: {
      textAlign: "center",
      padding: theme.spacing(4),
      backgroundColor: theme.palette.grey[50],
      borderRadius: 8,
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
      width: "100%",
    },
  })
);

const currency = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const isVista = (payCond?: number) => payCond === 0;

const PixPaymentPanel: React.FC<PixPaymentPanelProps> = ({
  razaoSocial,
  produto,
  valorTotal = 0,
  payCond,
  payloads,
  loading,
}) => {
  const flags = useFlags(["pix_method_paycond"]);

  const classes = useStyles();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const allPaid = useMemo(
    () => payloads.length > 0 && payloads.every((p) => p.status === "pago"),
    [payloads]
  );

  if (!flags.pix_method_paycond.enabled) return null;

  // Note regra: só exibe se for à vista
  if (!isVista(payCond)) return null;

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1300);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1300);
    }
  };

  const hasPayloads = payloads && payloads.length > 0;

  return (
    <Box className={classes.root}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Pagamento via Pix
          </Typography>
          <Typography variant="body2" className={classes.subHeader}>
            Razão Social: <b>{razaoSocial}</b> • Produto: <b>{produto}</b> •
            Valor Total: <b>{currency(valorTotal)}</b>
          </Typography>
        </Box>

        {!loading && hasPayloads && (
          <Chip
            label={allPaid ? "Pagamento confirmado" : "Pagamento pendente"}
            className={allPaid ? classes.statusPaid : classes.statusPending}
          />
        )}
      </Box>

      <Divider style={{ margin: "8px 0 16px" }} />

      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress color="primary" size={60} />
        </Box>
      ) : (
        <>
          {!hasPayloads ? (
            <Box className={classes.noCodesMessage}>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ fontWeight: 600 }}
              >
                ⚠️ Não existem códigos de pagamento Pix disponíveis para este
                pedido.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Por favor, entre em contato com o suporte ou tente novamente
                mais tarde.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {payloads.map((px) => {
                const paid = px.status === "pago";
                return (
                  <Grid item xs={12} md={3} key={px.id}>
                    <Box className={classes.card}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">
                          QR Code #{px.id}
                        </Typography>
                        <Chip
                          size="small"
                          label={paid ? "pago" : "pendente"}
                          className={
                            paid ? classes.statusPaid : classes.statusPending
                          }
                        />
                      </Box>

                      <Box display="flex" justifyContent="center" my={2}>
                        <QRCodeCanvas
                          value={px.copiaECola}
                          size={184}
                          includeMargin
                        />
                      </Box>

                      <Typography variant="body2" gutterBottom>
                        Pix copia e cola:
                      </Typography>
                      <Box className={classes.codeBox}>{px.copiaECola}</Box>

                      <Box className={classes.actions}>
                        <Tooltip title={paid ? "Já pago" : "Copiar código Pix"}>
                          <span>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<CopyIcon />}
                              onClick={() => handleCopy(px.copiaECola, px.id)}
                              disabled={paid}
                            >
                              {copiedId === px.id ? "Copiado!" : "Copiar Pix"}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default PixPaymentPanel;
